import { Switch } from '../../../../components';
import { createQuestionFromConfiguration } from '../../../logbook/views/QuestionView';
import {
    AudioPlayer,
    Card,
    Exercise,
    LabelBody,
    LabelBodyHeadline,
    LabelBodySmall,
    LabelDisplay2,
    SecondaryButton,
    Spacer,
    Textarea,
    Tip,
} from '../../components';
import { IllustrationPenPaper } from '../../icons';
import { ExerciseDto, ExerciseStatus, Question } from '../../interfaces';
import { normalizeInput, useSyncedState } from '../../useSyncedState';
import { ExerciseData, ExerciseViewProps } from '../exercises';

const page1RecapInputKey = 'exercise213Page1RecapInputKey';

export const exerciseData213: ExerciseData = {
    ExerciseView: Module2S1Exercise3,
    dataKeys: [page1RecapInputKey],
    questions: [
        createQuestionFromConfiguration({
            title: 'Spænd op og spænd af',
            description:
                'Hvad skete der med tankerne imens du lyttede til lydklippet?',
            dataKeys: [page1RecapInputKey],
        }),
    ],
};

export function Module2S1Exercise3({
    exercise,
    onContinue,
}: ExerciseViewProps) {
    const [recapInput, setRecapInput] = useSyncedState(
        exercise.Data[page1RecapInputKey] ?? '',
    );
    return (
        <Exercise title="Afspænding">
            <Switch
                value={exercise.CurrentPage}
                default={1}
                cases={{
                    1: (
                        <Page1
                            exercise={exercise}
                            recapInput={recapInput}
                            onChangeRecapInput={setRecapInput}
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 2,
                                    data: {
                                        [page1RecapInputKey]:
                                            normalizeInput(recapInput),
                                    },
                                })
                            }
                        />
                    ),
                    2: (
                        <Page2
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.Completed,
                                    goToNext: '2.1.completed',
                                })
                            }
                        />
                    ),
                }}
            />
        </Exercise>
    );
}

type Page1Props = {
    exercise: ExerciseDto;
    recapInput: string;
    onChangeRecapInput: (value: string) => void;
    onContinue: () => void;
};
function Page1({
    exercise,
    recapInput,
    onChangeRecapInput,
    onContinue,
}: Page1Props) {
    return (
        <>
            <LabelDisplay2>Spænd op og spænd af</LabelDisplay2>
            <LabelBody class="my-10">
                Når tankerne kører i ring, og de er svære at stoppe, vil kroppen
                ofte automatisk være spændt. Tit lægger vi slet ikke mærke til,
                at vi spænder op i ballen, i hånden eller i kæben.
            </LabelBody>
            <LabelBody>
                Ved først at spænde op og derefter slappe af i en muskel, kan du
                få mere ro på både kroppen og tankerne. Find et uforstyrret sted
                og start lydklippet, når du er klar.
            </LabelBody>
            <AudioPlayer
                exercise={exercise}
                src="https://static.hejdoktor.dk/falcksundtsind/AFSPÆNDING+02+MIX.mp3"
                duration="00:12:11"
                type="audio/mpeg"
                className="my-30"
            >
                <LabelBodySmall>Mindfulness</LabelBodySmall>
                <LabelBodyHeadline>Afspænding</LabelBodyHeadline>
            </AudioPlayer>
            <Card>
                <IllustrationPenPaper />
                <LabelDisplay2 class="mb-10 mt-20">
                    Hvordan gik det?
                </LabelDisplay2>
                <LabelBody>Hvad skete der med tankerne imens?</LabelBody>
                <Textarea text={recapInput} onChangeText={onChangeRecapInput} />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer class="mt-30" />
            <SecondaryButton onClick={onContinue}>fortsæt</SecondaryButton>
        </>
    );
}

type Page2Props = {
    onContinue: () => void;
};
function Page2({ onContinue }: Page2Props) {
    return (
        <>
            <LabelDisplay2 class="mb-10">Et godt redskab</LabelDisplay2>
            <LabelBody class="mb-10">
                Når vi igennem kroppen fortæller hjernen, at det er ok at give
                slip og slappe af, bliver det muligt at lade tankerne passere
                forbi. Det gør det muligt at være mere til stede.
            </LabelBody>
            <LabelBody class="mb-30">
                Afspændingsøvelsen kan give en afslappethed nu og her, men det
                kan være godt at øve sig flere gange. Over tid kan du blive mere
                bevidst om, hvornår du er anspændt, og vide at det kan hjælpe at
                spænde af.
            </LabelBody>
            <Tip>
                Hvis du har svært ved at sove, kan teknikken være en hjælp, når
                du ligger i sengen.
            </Tip>
            <Spacer class="mt-30" />
            <SecondaryButton onClick={onContinue}>
                Afslut øvelsen
            </SecondaryButton>
        </>
    );
}
